import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, Copy, Undo2 } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  MarketingTrigger,
  TriggerCandidate,
} from "@shared-core/data/marketing-types";
import {
  CANDIDATE_GROUP_LABELS,
  LEFT_OUT_REASON_LABELS,
  countToSend,
  groupTriggerCandidates,
} from "@shared-core/domain/marketing-triggers";
import {
  formatDateOrDash,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import { newUuid } from "@shared-core/domain/object-ui/command-run";
import { paths } from "@shared-core/routing/routes";
import { useCanManageMarketing } from "../app/auth/hooks";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../app/components/primitives/alert-dialog";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import { Label } from "../app/components/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../app/components/primitives/select";
import { Switch } from "../app/components/primitives/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../app/components/primitives/tabs";
import { TriggerStatusBadge } from "./MarketingTriggersPage";

/**
 * One triggered email: today's list to check and approve, what it has sent,
 * and its settings. See docs/features/triggered-emails/01_product_brief.md.
 */
export function MarketingTriggerPage() {
  const { triggerId = "" } = useParams<{ triggerId: string }>();
  const dataAdapter = useDataAdapter();
  const canManage = useCanManageMarketing();
  const [trigger, setTrigger] = useState<MarketingTrigger | null>(null);
  const [error, setError] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setTrigger(await dataAdapter.getMarketingTrigger(triggerId));
    } catch (e) {
      console.error("marketing.trigger.load.failed", e);
      setError(true);
    }
  }, [dataAdapter, triggerId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function setStatus(status: MarketingTrigger["status"]) {
    try {
      setTrigger(
        await dataAdapter.updateMarketingTrigger(triggerId, { status }),
      );
    } catch (e) {
      console.error("marketing.trigger.status_change.failed", e);
      setError(true);
    }
  }

  return (
    <div className="space-y-6">
      <MarketingNav active="triggers" />
      <Link
        to={paths.marketingTriggers()}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Triggers
      </Link>

      {error && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600 dark:text-red-400">
          Couldn't load this trigger. Please try again.
        </div>
      )}
      {!error && trigger === null && (
        <div className="text-sm text-muted-foreground">Loading trigger…</div>
      )}

      {trigger && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl">{trigger.name}</h1>
                <TriggerStatusBadge status={trigger.status} />
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {trigger.description}
              </p>
            </div>
            {canManage &&
              (trigger.status === "live" ? (
                <Button variant="outline" onClick={() => setPauseOpen(true)}>
                  Pause
                </Button>
              ) : (
                <Button onClick={() => void setStatus("live")}>
                  Switch back on
                </Button>
              ))}
          </div>

          <Tabs defaultValue="today" className="space-y-4">
            <TabsList>
              <TabsTrigger value="today">
                Today's list
                {trigger.status === "live" && trigger.today_to_check > 0
                  ? ` (${trigger.today_to_check})`
                  : ""}
              </TabsTrigger>
              <TabsTrigger value="sent">Sent</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
            <TabsContent value="today">
              <TodaysList
                trigger={trigger}
                canManage={canManage}
                onApproved={setTrigger}
              />
            </TabsContent>
            <TabsContent value="sent">
              <SentList trigger={trigger} />
            </TabsContent>
            <TabsContent value="settings">
              <TriggerSettings
                trigger={trigger}
                canManage={canManage}
                onSaved={setTrigger}
              />
            </TabsContent>
          </Tabs>

          <AlertDialog open={pauseOpen} onOpenChange={setPauseOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Pause {trigger.name}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Nobody will be picked while it's paused. People who qualify in
                  the meantime won't get this email unless, when you switch it
                  back on, "Also include people who qualified in the last [ ]
                  days" covers the days it was paused.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it live</AlertDialogCancel>
                <AlertDialogAction onClick={() => void setStatus("paused")}>
                  Pause
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}

function TodaysList({
  trigger,
  canManage,
  onApproved,
}: {
  trigger: MarketingTrigger;
  canManage: boolean;
  onApproved: (t: MarketingTrigger) => void;
}) {
  const dataAdapter = useDataAdapter();
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  // "Needs you" rows start on the account's main contact, for the user to
  // approve or change (decided 16 Oct 2026).
  const [picked, setPicked] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      trigger.today
        .filter((c) => c.state === "needs_you" && c.suggested_person_id)
        .map((c) => [c.firing_id, c.suggested_person_id as string]),
    ),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  // One per confirmation, so a double click (or a retry of the same dialog)
  // sends once.
  const [requestId, setRequestId] = useState(newUuid);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (trigger.status === "paused") {
    return (
      <Notice>Paused. Nobody is picked while this trigger is paused.</Notice>
    );
  }

  const groups = groupTriggerCandidates(trigger.today);
  const sendCount = countToSend(trigger.today, removed, picked);
  const listDate = formatDateOrDash(trigger.list_date);

  if (trigger.approved_today) {
    return (
      <div className="space-y-4">
        <Notice tone="done">
          Today's list was approved by {trigger.approved_today.by} at{" "}
          {formatDateTimeOrDash(trigger.approved_today.at)}.{" "}
          {trigger.approved_today.sent_count === 1
            ? "1 person was"
            : `${trigger.approved_today.sent_count} people were`}{" "}
          tagged in Mailchimp, and the flow emails them. See the Sent tab.
        </Notice>
      </div>
    );
  }

  if (trigger.today.length === 0) {
    return <Notice>Nobody qualified for {listDate}.</Notice>;
  }

  async function approve() {
    setSaving(true);
    setError(null);
    try {
      onApproved(
        await dataAdapter.approveTriggerList(trigger.trigger_id, {
          removed_firing_ids: [...removed],
          picked_contacts: picked,
          request_id: requestId,
        }),
      );
    } catch (e) {
      console.error("marketing.trigger.approve.failed", e);
      setError(
        "Couldn't approve today's list. Nothing was sent. Please try again.",
      );
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        People who qualified for {listDate}.{" "}
        {trigger.check_before_sending
          ? "Nothing is sent until you approve the list."
          : "This trigger sends automatically; the list is a record of who it picked."}
      </p>

      <CandidateGroup
        title={CANDIDATE_GROUP_LABELS.to_send}
        count={groups.to_send.length}
      >
        {groups.to_send.length === 0 ? (
          <EmptyGroup>Nobody to send to.</EmptyGroup>
        ) : (
          groups.to_send.map((c) => {
            const isRemoved = removed.has(c.firing_id);
            return (
              <CandidateRow key={c.firing_id} candidate={c} muted={isRemoved}>
                {canManage && trigger.check_before_sending && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setRemoved((prev) => {
                        const next = new Set(prev);
                        if (isRemoved) next.delete(c.firing_id);
                        else next.add(c.firing_id);
                        return next;
                      })
                    }
                  >
                    {isRemoved ? (
                      <>
                        <Undo2 className="size-4" />
                        Put back
                      </>
                    ) : (
                      "Remove"
                    )}
                  </Button>
                )}
              </CandidateRow>
            );
          })
        )}
      </CandidateGroup>

      {groups.needs_you.length > 0 && (
        <CandidateGroup
          title={CANDIDATE_GROUP_LABELS.needs_you}
          count={groups.needs_you.length}
          hint="One couldn't tell who placed the order, so One has chosen the account's main contact. Check the contact, pick someone else, or choose Don't send."
        >
          {groups.needs_you.map((c) => (
            <CandidateRow key={c.firing_id} candidate={c}>
              {canManage && (
                <Select
                  value={picked[c.firing_id] ?? "none"}
                  onValueChange={(v) =>
                    setPicked((prev) => {
                      const next = { ...prev };
                      if (v === "none") delete next[c.firing_id];
                      else next[c.firing_id] = v;
                      return next;
                    })
                  }
                >
                  <SelectTrigger
                    className="w-96 max-w-full"
                    aria-label={`Who to send to at ${c.account_name}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Don't send</SelectItem>
                    {c.contact_options.map((o) => (
                      <SelectItem key={o.person_id} value={o.person_id}>
                        {o.name} ({o.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </CandidateRow>
          ))}
        </CandidateGroup>
      )}

      {groups.left_out.length > 0 && (
        <CandidateGroup
          title={CANDIDATE_GROUP_LABELS.left_out}
          count={groups.left_out.length}
          hint="These people qualified but won't be emailed."
        >
          {groups.left_out.map((c) => (
            <CandidateRow key={c.firing_id} candidate={c} muted>
              <span className="text-sm text-muted-foreground">
                {c.left_out_reason
                  ? LEFT_OUT_REASON_LABELS[c.left_out_reason]
                  : ""}
              </span>
            </CandidateRow>
          ))}
        </CandidateGroup>
      )}

      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* A trigger that sends without checking still waits for a person on
          its "Needs you" rows: they are never sent automatically. */}
      {canManage &&
        (trigger.check_before_sending || groups.needs_you.length > 0) && (
          <div className="flex justify-end">
            <Button
              disabled={sendCount === 0 || saving}
              onClick={() => {
                setRequestId(newUuid());
                setConfirmOpen(true);
              }}
            >
              Approve and send {sendCount}
            </Button>
          </div>
        )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Send {trigger.name} to{" "}
              {sendCount === 1 ? "1 person" : `${sendCount} people`}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              They'll be tagged {trigger.mailchimp_tag} in Mailchimp, and the
              flow emails them straight away. An email can't be taken back once
              it's sent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={() => void approve()}>
              Approve and send
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SentList({ trigger }: { trigger: MarketingTrigger }) {
  if (trigger.sent.length === 0) {
    return <Notice>Nothing sent yet.</Notice>;
  }
  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Sent</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Approved by</TableHead>
              <TableHead>Opened</TableHead>
              <TableHead>Ordered since</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {trigger.sent.map((s) => (
              <TableRow key={`${s.email}-${s.sent_at}`}>
                <TableCell>{formatDateOrDash(s.sent_at)}</TableCell>
                <TableCell>
                  <div>{s.person_name}</div>
                  <div className="text-xs text-muted-foreground">{s.email}</div>
                </TableCell>
                <TableCell>
                  {s.account_name}{" "}
                  <span className="text-xs text-muted-foreground">
                    ({s.account_code})
                  </span>
                </TableCell>
                <TableCell>{s.approved_by ?? "Sent automatically"}</TableCell>
                <TableCell>
                  {s.opened === null ? (
                    <span className="text-muted-foreground">Not known yet</span>
                  ) : s.opened ? (
                    "Yes"
                  ) : (
                    "No"
                  )}
                </TableCell>
                <TableCell>{s.ordered_since ? "Yes" : "Not yet"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">
        Opened comes from Mailchimp the morning after a send. Ordered since
        counts any order from the account after the email.
      </p>
    </div>
  );
}

function TriggerSettings({
  trigger,
  canManage,
  onSaved,
}: {
  trigger: MarketingTrigger;
  canManage: boolean;
  onSaved: (t: MarketingTrigger) => void;
}) {
  const dataAdapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [fromName, setFromName] = useState(trigger.from_name);
  const [checkFirst, setCheckFirst] = useState(trigger.check_before_sending);
  const [catchUp, setCatchUp] = useState(String(trigger.catch_up_days));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const isDirty =
    fromName !== trigger.from_name ||
    checkFirst !== trigger.check_before_sending ||
    catchUp !== String(trigger.catch_up_days);

  async function save() {
    const days = Number(catchUp);
    if (!Number.isInteger(days) || days < 0 || days > 30) {
      setMessage("Enter a number of days between 0 and 30.");
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      onSaved(
        await dataAdapter.updateMarketingTrigger(trigger.trigger_id, {
          from_name: fromName.trim(),
          check_before_sending: checkFirst,
          catch_up_days: days,
        }),
      );
      setMessage("Saved.");
    } catch (e) {
      console.error("marketing.trigger.settings_save.failed", e);
      setMessage("Couldn't save the settings. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-xl space-y-5">
      <div className="space-y-1">
        <Label>Mailchimp tag</Label>
        <div className="flex items-center gap-2">
          <code className="rounded border border-border bg-muted px-2 py-1 text-sm">
            {trigger.mailchimp_tag}
          </code>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              void capabilities
                .copyToClipboard(trigger.mailchimp_tag)
                .then(() => setCopied(true))
            }
          >
            <Copy className="size-4" />
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Studio starts the Mailchimp flow on this tag ("Tag added", audience
          wearepollards marketing).
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor="trigger-from">From</Label>
        <Input
          id="trigger-from"
          value={fromName}
          disabled={!canManage}
          onChange={(e) => setFromName(e.target.value)}
        />
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <Label htmlFor="trigger-check-first">Check before sending</Label>
          <p className="text-xs text-muted-foreground">
            When on, nothing is sent until you approve each day's list. When
            off, the list goes automatically and stays here as a record.
          </p>
        </div>
        <Switch
          id="trigger-check-first"
          checked={checkFirst}
          disabled={!canManage}
          onCheckedChange={setCheckFirst}
        />
      </div>

      <div className="space-y-1">
        <Label htmlFor="trigger-catch-up">
          Also include people who qualified in the last [ ] days
        </Label>
        <Input
          id="trigger-catch-up"
          inputMode="numeric"
          className="w-24"
          value={catchUp}
          disabled={!canManage}
          onChange={(e) => setCatchUp(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          So nobody is missed when an order reaches One late, or after the
          trigger has been paused. Nobody ever gets the same trigger twice.
        </p>
      </div>

      {canManage && (
        <div className="flex items-center gap-3">
          <Button disabled={!isDirty || saving} onClick={() => void save()}>
            Save settings
          </Button>
          {message && (
            <span
              className={`text-sm ${message === "Saved." ? "text-muted-foreground" : "text-red-600 dark:text-red-400"}`}
            >
              {message}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function CandidateGroup({
  title,
  count,
  hint,
  children,
}: {
  title: string;
  count: number;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold">
        {title} ({count})
      </h2>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <div className="divide-y divide-border rounded-lg border border-border">
        {children}
      </div>
    </section>
  );
}

function CandidateRow({
  candidate,
  muted = false,
  children,
}: {
  candidate: TriggerCandidate;
  muted?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <div className={`min-w-0 ${muted ? "opacity-60" : ""}`}>
        <div className="text-sm font-medium">
          {candidate.person_name ?? "Who to send to?"}
          <span className="font-normal text-muted-foreground">
            {" "}
            · {candidate.account_name} ({candidate.account_code})
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          {candidate.email ? `${candidate.email} · ` : ""}
          {candidate.reason}
          {candidate.campaign_cap_note &&
            " · Had 3+ campaigns in the last 30 days"}
        </div>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function EmptyGroup({ children }: { children: ReactNode }) {
  return (
    <div className="px-4 py-3 text-sm text-muted-foreground">{children}</div>
  );
}

function Notice({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "done";
}) {
  return (
    <div
      className={`rounded-lg border p-4 text-sm ${
        tone === "done"
          ? "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100"
          : "border-border text-muted-foreground"
      }`}
    >
      {children}
    </div>
  );
}
